import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Spinner from '../../Spinner';

import Button from '../../form/Button';
import { ModalPaymentMethodsType } from '../../../enums';
import { FormattedMessage } from 'react-intl';
import MercadopagoForm from './MercadopagoForm';
import { getImagePath, shouldApplyCouponToShoppingCart } from '../../../utils';
import SubPaymentMethodModal from './Modal';

const CardForm = ({
  config,
  paymentConfig,
  history,
  userData,
  handleSubmit,
  intl,
  client,
  transformEntities,
  deviceCountry,
  couponApply,
  goBack,
  currentSubPaymentMethod,
  currentPaymentMethod,
  shoppingCart,
  countryCode,
  searchParams,
  ...props
}) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [modal, setModal] = useState(null);

  const handleRedirect = () => {
    const cloudpayConfig = window.AppENV.cloudpay;
    const url = `${cloudpayConfig.baseURL}/redirect/goBackToReturnUrl${window.location.search
      }&jwt=${localStorage.getItem('jwt')}`;
    window.location.href = url;
  };

  const handleCloseModal = () => setModal(null);

  const handleTimeOutredirect = () =>
    history.replace(
      `/${client}/subscribe/subscriptions${history.location.search}`
    );

  const onSubmitForm = useCallback(async (cardFormData) => {
    if (isSubmitting) return;

    setSubmitting(true);
    try {
      const token = cardFormData?.token;
      if (!token) {
        const err = new Error('Token not generated');
        err.code = 'BRICK_FORM_INVALID';
        throw err;
      }

      const isDirectFlow = currentSubPaymentMethod?.allowed_flows?.includes('DIRECT');
      const type = currentSubPaymentMethod.type;

      const response = await handleSubmit({
        token,
        email: userData.data.email,
        paymentMethodId: currentPaymentMethod.toUpperCase(),
        paymentMethodType: type,
        paymentMethodFlow: isDirectFlow ? 'DIRECT' : 'REDIRECT',
        country: countryCode,
        ...(shouldApplyCouponToShoppingCart(couponApply, shoppingCart) ? { couponCode: couponApply.code } : {}),
        ...(searchParams.promoSlug && { promoSlug: searchParams.promoSlug }),
      });

      setModal({
        code: 200,
        img: getImagePath('ic_approved.svg', client),
        transaction: response,
        forceToChangeToFavorite: props.forceToChangeToFavorite,
        default: 'default',
        handleClick: props.handleRedirect || handleRedirect,
        handleCloseModal: () => {
          transformEntities({
            subscriber: { subscription: { ignoreRedirect: false } },
          });
          history.replace(
            `/${client}/subscribe/subscriptions/management${history.location.search}`
          );
        },
      });
console.log('FROMM TERMIUANDO')
      return response;
    } catch (err) {
      console.error('Payment error:', err);
      setModal({
        code: 'rejected',
        error: true,
        img: getImagePath('ic_rejected.svg'),
        default: 'error',
        ...(err?.body?.message && { message: err?.body?.message }),
        handleClick: err?.status >= 500 ? handleTimeOutredirect : handleCloseModal,
      });
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [
    isSubmitting,
    currentSubPaymentMethod,
    currentPaymentMethod,
    handleSubmit,
    userData,
    countryCode,
    couponApply,
    searchParams,
    client,
    props.forceToChangeToFavorite,
    props.handleRedirect,
    transformEntities,
    history,
    handleTimeOutredirect
  ]);

  const onReady = useCallback(() => {
    setShowButtons(true);
  }, []);

  const onSubmitFormRef = useRef(onSubmitForm);
  useEffect(() => {
    onSubmitFormRef.current = onSubmitForm;
  }, [onSubmitForm]);

  // 2) Crea un callback ESTABLE que el Brick usará siempre (no cambia su identidad)
  const onSubmitStable = useCallback((formData) => {
    return onSubmitFormRef.current(formData);
  }, []);


  const submitLabel = intl.formatMessage({
    id:
      props.modalType === ModalPaymentMethodsType.NEW_PAYMENT_METHOD
        ? 'Form.label.addPaymentMethod'
        : 'Form.label.submit',
  });

  if (!shoppingCart?.productsToBuy[0]?.price || !userData?.data?.email) {
    return <Spinner mauto />;
  }

  return (
    <>
      <SubPaymentMethodModal entity="card" intl={intl} modal={modal} />
      <div className='mercadopago-main-container'>
        <div className='mercadopago-container'>
          <MercadopagoForm
            amount={shoppingCart.productsToBuy[0].price}
            email={userData.data.email}
            onSubmit={onSubmitStable}
            onReady={onReady}
            submitLabel={submitLabel}
          />
        </div>
        {showButtons && (
          <div className='buttons-mercadopago'>
            {goBack && (
              <Button
                type='button'
                className='empty'
                onClick={goBack}
                disabled={isSubmitting}
              >
                <FormattedMessage id='Form.label.cancel' />
              </Button>
            )}
          </div>
        )}
      </div>
    </>

  );
};

CardForm.propTypes = {
  client: PropTypes.string.isRequired,
  paymentConfig: PropTypes.object.isRequired,
  config: PropTypes.shape({
    sandbox: PropTypes.bool,
    IDPs: PropTypes.array,
    shortCode: PropTypes.string,
    products: PropTypes.array,
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  transformEntities: PropTypes.func.isRequired,
  forceToChangeToFavorite: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  deviceCountry: PropTypes.string,
  couponApply: PropTypes.any,
  countryCode: PropTypes.string.isRequired,
  goBack: PropTypes.func,
  currentSubPaymentMethod: PropTypes.shape({
    paymentMethod: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    logo: PropTypes.string,
    allowed_flows: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  userData: PropTypes.shape({
    country: PropTypes.string,
    country_code: PropTypes.string,
    user_token: PropTypes.string,
    data: PropTypes.shape({
      email: PropTypes.string.isRequired,
    }).isRequired,
  }),
  currentPaymentMethod: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    goBack: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
  }).isRequired,
  shoppingCart: PropTypes.object,
  modalType: PropTypes.string,
  handleRedirect: PropTypes.func,
};
export default CardForm;